import { Injectable } from '@angular/core';
import { CTacticalSituation } from '@models/imported/SagaSchema/CTacticalSituation';
import { LayoutService } from '@services/layout/layout.service';
import { ProfileService } from '@services/profile/profile.service';
import { AddinExecute, Execute } from '@techwan/ionic-core';
import { GroupName, SagaLayerManager } from '@techwan/ionic-core/map';
import { GroupName as GrpName, TacticalSituationLayer } from '@techwan/mapping';
import { Map as SagaMap } from '@techwan/mapping/lib/src/main/map';
import { DEFAULT_IMAGE_SIZE } from '@utils/image';
import Group from 'ol/layer/group';
import { Subscription } from 'rxjs';
import { filter, first } from 'rxjs/operators';
import { DrawingEventType } from '../../../schema/event-type';
import { ILayerController } from '../../../schema/interfaces/ILayerController';
import { ITacticalDrawingEvent } from '../../../schema/interfaces/ITacticalDrawingEvent';
import { MyCardDrawings } from '../../../schema/my-card-drawings';
import { MyDrawing } from '../../../schema/my-drawing';
import { LayerControllerService } from '../../layer-controller/layer-controller.service';
import { TacticalDrawingsService } from '../../tactical-drawings/tactical-drawings.service';

@AddinExecute({
  name: 'drawings-layer'
})
@Injectable()
export class DrawingsLayerControllerService extends Execute implements ILayerController {
  private map: SagaMap = null;

  private myDrawingLayer: MyDrawing = null;
  private myCardDrawings: MyCardDrawings = null;
  private sagaLayerManager: SagaLayerManager = null;

  private subs: Subscription[] = [];

  constructor(
    private layoutService: LayoutService,
    private profileService: ProfileService,
    private layerController: LayerControllerService,
    private tactialDrawings: TacticalDrawingsService
  ) {
    super();
  }

  execute() {
    this.profileService.ready
      .pipe(
        filter(isReady => isReady),
        first()
      )
      .subscribe(() => this.onProfileReady());
  }

  private onProfileReady() {
    const tacticalDrawingsEnabled = this.layoutService.layout.map && this.layoutService.layout.map === 'Map/Full';

    if (tacticalDrawingsEnabled) {
      this.myDrawingLayer = this.tactialDrawings.getMyDrawings();
      this.myCardDrawings = this.tactialDrawings.getDrawingsOfMyCard();

      this.layerController.addController(this);
    }
  }

  mapDidChange(sagaMap: SagaMap) {
    if (sagaMap && this.map === null) {
      this.map = sagaMap;
      this.setup();
    } else if (!sagaMap) {
      this.cleanup();
      this.map = null;
    }
  }

  private setup() {
    this.sagaLayerManager = this.map.getLayerManager() as SagaLayerManager;

    this.subs.push(this.myDrawingLayer.$change.subscribe(tacticalSituation => this.refreshMyDrawings(tacticalSituation)));
    this.subs.push(this.myCardDrawings.$change.subscribe(tacticalSituation => this.refreshMyCardDrawings(tacticalSituation)));

    const groupDrawings = this.sagaLayerManager.getLayerGroup(GrpName.drawingLayers as any);

    this.myCardDrawings.tacticalSituations.forEach(tacticalDrawing => {
      this.createLayer(this.sagaLayerManager, groupDrawings, tacticalDrawing);
    });
  }

  private refreshMyCardDrawings(event: ITacticalDrawingEvent) {
    const drawingLayersGroup = this.sagaLayerManager.getLayerGroup(GrpName.drawingLayers as any);
    const drawingLayers = drawingLayersGroup.getLayers() as ol.Collection<TacticalSituationLayer>;
    switch (event.type) {
      case DrawingEventType.add:
        this.createLayer(this.sagaLayerManager, drawingLayersGroup, event.target);
        break;

      case DrawingEventType.remove:
        drawingLayers.remove(this.find(drawingLayers, event.target));
        break;

      case DrawingEventType.update:
        this.find(drawingLayers, event.target).loadGeometry(event.target.GeometryObjects);
        break;
    }
  }

  private refreshMyDrawings(tacticalSituation: CTacticalSituation) {
    const editDrawingGroup = this.sagaLayerManager.getLayerGroup(GroupName.editDrawing);
    const drawingLayers = editDrawingGroup.getLayers();
    const currentLayer = drawingLayers.getArray().find(layer => layer instanceof TacticalSituationLayer) || null;

    if (tacticalSituation === null && currentLayer) {
      drawingLayers.remove(currentLayer);
    } else if (currentLayer === null && tacticalSituation !== null) {
      this.createLayer(this.sagaLayerManager, editDrawingGroup, tacticalSituation);
    } else if (tacticalSituation && currentLayer) {
      (currentLayer as TacticalSituationLayer).loadGeometry(tacticalSituation.GeometryObjects);
    }
  }

  private find(drawings: ol.Collection<TacticalSituationLayer>, tacticalSituation: CTacticalSituation): TacticalSituationLayer {
    return drawings.getArray().find((layer: TacticalSituationLayer) => layer.getId() === tacticalSituation.ObjGuid) || null;
  }

  private createLayer(
    sagaLayerManager: SagaLayerManager,
    destinationGroup: Group,
    tacticalSituation: CTacticalSituation
  ): TacticalSituationLayer {
    const tacticalSituationLayer = sagaLayerManager.createDrawingLayerIn(destinationGroup);
    tacticalSituationLayer.set('name', 'MyDrawings');
    tacticalSituationLayer.setId(tacticalSituation.ObjGuid);
    tacticalSituationLayer.set('iconSize', DEFAULT_IMAGE_SIZE);
    tacticalSituationLayer.loadGeometry(tacticalSituation.GeometryObjects);
    return tacticalSituationLayer;
  }

  private cleanup() {
    while (this.subs.length > 0) {
      this.subs.pop().unsubscribe();
    }
  }
}
